import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import SEO from "../components/SEO";

export default function Login() {
  const { login, setInitialPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const getRedirectPath = (role: string) => {
    switch (role) {
      case "admin": return "/admin";
      default: return "/products";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate(getRedirectPath(result.user?.role || "customer"));
    } else if (result.requiresPasswordChange) {
      setRequiresPasswordChange(true);
      setError("");
    } else {
      setError(result.error || "Login failed");
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    setLoading(true);
    const result = await setInitialPassword(email, password, newPassword);
    setLoading(false);
    if (result.success) {
      navigate(getRedirectPath(result.user?.role || "customer"));
    } else {
      setError(result.error || "Failed to set password");
    }
  };

  if (requiresPasswordChange) {
    return (
      <>
        <SEO title="Set New Password" noindex />
        <section className="min-h-[80vh] flex items-center justify-center py-12 bg-background">
          <div className="card max-w-md w-full mx-4">
            <div className="text-center mb-8">
              <img src="/logo.svg" alt="Premier Pharma" className="h-10 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-primary">Set Your Password</h1>
              <p className="text-gray-500 text-sm mt-1">
                Your account requires a new password before you can continue.
              </p>
            </div>

            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">{error}</div>}

            <form onSubmit={handleSetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">New Password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  className="input-field"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Confirm New Password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  className="input-field"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your new password"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                {loading ? "Saving..." : "Set Password & Log In"}
              </button>
            </form>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO title="Customer Login" description="Sign in to your Premier Pharma wholesale account to view pricing, place orders, and manage invoices." />
      <section className="min-h-[80vh] flex items-center justify-center py-12 bg-background">
        <div className="card max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <img src="/logo.svg" alt="Premier Pharma" className="h-10 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary">Welcome Back</h1>
            <p className="text-gray-500 text-sm mt-1">Log in to your account</p>
          </div>

          {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Email</label>
              <input type="email" required className="input-field" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-primary">Password</label>
                <Link to="/forgot-password" className="text-xs text-accent hover:underline">Forgot Password?</Link>
              </div>
              <input type="password" required className="input-field" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50">
              {loading ? "Logging in..." : "Log In"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Don't have an account? <Link to="/register" className="text-accent font-medium hover:underline">Sign up</Link>
          </p>
        </div>
      </section>
    </>
  );
}
